import { FC, KeyboardEvent, useMemo, useRef } from 'react';
import { ReactComponent as Arrow } from 'images/arrow.svg';
import { useOutsideClick } from 'lib/hooks/useOutsideClick';
import { useToggle } from 'lib/hooks/useToggle';
import s from './PaymentSelect.module.css';
import { Button } from 'components/common/Button';

type PaymentSelectOption = {
  id: string;
  value: string;
  label: string;
};

type PaymentSelectProps = {
  value: string;
  options: PaymentSelectOption[];
  ariaLabel: string;
  className?: string;
  variant?: 'language' | 'tariff';
  onChange: (value: string) => void;
};

const icons = {
  arrow: <Arrow aria-hidden='true' />,
};

export const PaymentSelect: FC<PaymentSelectProps> = ({
  value,
  options,
  ariaLabel,
  className,
  variant = 'language',
  onChange,
}) => {
  const refSelect = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useToggle(false);

  const selectedOption = useMemo(
    () =>
      options.find((option) => option.value === value) ?? options[0] ?? null,
    [options, value],
  );

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSelect = (nextValue: string) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault();
      setIsOpen(true);
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  useOutsideClick({
    ref: refSelect,
    handlerClose: handleClose,
    attached: isOpen,
  });

  return (
    <div
      ref={refSelect}
      className={`${s['payment-select']} ${className ?? ''}`}
      data-open={isOpen}
      data-variant={variant}
    >
      <Button
        type='button'
        className={s['payment-select__btn']}
        aria-label={ariaLabel}
        aria-haspopup='listbox'
        aria-expanded={isOpen}
        onClick={handleToggle}
        onKeyDown={handleKeyDown}
      >
        <span className={s['payment-select__value']}>
          {selectedOption?.value}
        </span>

        <span className={s['payment-select__arrow']} data-open={isOpen}>
          {icons.arrow}
        </span>
      </Button>

      {isOpen && (
        <ul
          className={s['payment-select__dropdown']}
          role='listbox'
          aria-label={ariaLabel}
        >
          {options.map((option) => {
            if (variant === 'tariff') {
              return (
                <TariffItem
                  key={option.id}
                  option={option}
                  value={value}
                  handleSelect={handleSelect}
                />
              );
            }
            return (
              <LangItem
                key={option.id}
                option={option}
                value={value}
                handleSelect={handleSelect}
              />
            );
          })}
        </ul>
      )}
    </div>
  );
};

type ItemProps = {
  option: PaymentSelectOption;
  value: string;
  handleSelect: (nextValue: string) => void;
};

const LangItem = ({ option, value, handleSelect }: ItemProps) => {
  return (
    <li
      className={s['payment-select__option']}
      role='option'
      onClick={() => handleSelect(option.id)}
      aria-selected={option.value === value}
      data-selected={option.value === value}
    >
      {option.value}
    </li>
  );
};

const TariffItem = ({ option, value, handleSelect }: ItemProps) => {
  return (
    <li
      className={`${s['payment-select__option']} ${s['payment-select__option_tariff']}`}
      role='option'
      onClick={() => handleSelect(option.id)}
      aria-selected={option.value === value}
      data-selected={option.value === value}
    >
      <span className={s['payment-select__option-name']}>{option.value}</span>
      <span className={s['payment-select__option-price']}>{option.label}</span>
    </li>
  );
};
